<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { LegalProcessesApi } from 'dashboard/api/legalProcesses';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import {
  ranquear,
  destacar,
} from 'dashboard/routes/dashboard/internal-chat/buscaRanking';
import { corDoNome, iniciaisDoNome } from 'dashboard/helper/avatar';
import { fileFormatFor } from 'dashboard/helper/legalFileFormat';
import { dataEscritorio } from 'dashboard/helper/fusoEscritorio';
import { moedaComSimbolo } from 'dashboard/helper/moeda';
import EsqueletoLista from 'dashboard/components-next/EsqueletoLista.vue';
import { useBuscaGlobal, ehAtalhoDeBusca } from './useBuscaGlobal';

/**
 * A BUSCA DO TOPO, EM MODAL.
 *
 * Victor, 10/09/2026: "essa lupa ai... acho muito feio e layout horrível, e
 * além de tudo não tem muita integração com nosso sistema (...) eu não quero
 * que me envie pra uma página, eu quero que abra um modal".
 *
 * Antes a lupa era um link para `/search`, a busca de CONVERSAS do Chatwoot:
 * trocava de tela e não sabia nada de cliente, pasta nem processo — que é o
 * que o escritório procura o dia inteiro.
 *
 * TRÊS DECISÕES QUE ESTE ARQUIVO CARREGA
 *
 * 1. **Nada de regra de busca aqui.** Quem sabe achar cliente é
 *    `LegalClient.buscando`, no servidor, a MESMA da lista de Clientes; quem
 *    ordena por relevância é `buscaRanking`, o mesmo do menu de barra do chat
 *    interno. Este componente só mostra.
 *
 * 2. **Os destinos chegam por `props`**, do mesmo `horizontalNavItems` que
 *    desenha os ícones da barra. Uma segunda lista de "para onde dá para ir"
 *    nasceria desatualizada no primeiro módulo novo.
 *
 * 3. **Corrida de resposta descartada.** Digitar "sou" → "souz" → "souza"
 *    dispara três buscas; se a primeira voltar por último, a tela mostra o
 *    resultado de "sou" com "souza" escrito no campo. O contador `geracao`
 *    resolve — mesmo remédio do `useSlashSearch`, e o defeito só aparece com
 *    rede ruim, que é justamente onde a pessoa está.
 */

const props = defineProps({
  // `horizontalNavItems` da barra: { name, label, icon, to, children }
  destinos: { type: Array, default: () => [] },
});

const { t } = useI18n();
const router = useRouter();
const { accountScopedRoute } = useAccount();
const { aberta, termoInicial, fechar } = useBuscaGlobal();

const B = 'BUSCA_GLOBAL';

// As teclas do rodapé vêm daqui, e não escritas no template: texto cru no
// template é barrado pelo lint de tradução, e com razão — no dia em que o
// atalho mudar, ele está num lugar só.
const TECLA_CIMA = '↑';
const TECLA_BAIXO = '↓';
const TECLA_ENTRA = '↵';
const TECLA_ESC = 'esc';

// Alinhado com a busca do chat interno: a pessoa aprende UM tempo de resposta
// no produto inteiro.
const DEBOUNCE_MS = 300;
const MIN_CARACTERES = 2;
// Quantos o servidor traz por grupo. Maior do que se mostra de propósito: ele
// casa pedaço em qualquer lugar e não sabe ordenar por relevância, então pedir
// 6 traria 6 casadas no meio e deixaria de fora a que começa com o termo.
const LIMITE_SERVIDOR = 12;
const LIMITE_POR_GRUPO = 6;

const termo = ref('');
const carregando = ref(false);
// A falha tem ramo próprio. Antes o `catch` só limpava a lista, e a tela caía
// em "Nada encontrado para …" — a frase oposta de "não consegui procurar".
const erro = ref(false);
// Os grupos que o servidor devolve (`Legal::BuscaGlobal::GRUPOS`), na ordem
// em que o advogado procura: gente e pasta primeiro, o trabalho do dia depois,
// o resto em seguida.
const GRUPOS = [
  'clientes',
  'processos',
  'atividades',
  'publicacoes',
  'documentos',
  'casos',
  'financeiro',
  'contratos',
  'contatos',
];
const gruposVazios = () => Object.fromEntries(GRUPOS.map(g => [g, []]));
const resultado = ref(gruposVazios());
const indiceAtivo = ref(0);
const campoRef = ref(null);
const listaRef = ref(null);

let temporizador = null;
let geracao = 0;

const limpar = () => {
  resultado.value = gruposVazios();
};

// ── o que cada grupo vira na tela ──────────────────────────────────────────
// Um adaptador por tipo, e não um `v-if` por linha no template: cada entidade
// tem formato próprio (cliente aninha o nome no contato, processo chama o
// título de `title`), e deixar a diferença vazar para o template obrigaria
// cada linha do menu a conhecer todas elas.
// Gente tem ROSTO, e não ícone: as iniciais na cor do nome, a mesma do avatar
// no resto do produto. Quem corre o olho acha "a Ana" pela cor antes de ler.
const rosto = nome => ({
  pessoa: true,
  iniciais: iniciaisDoNome(nome),
  cor: corDoNome(nome),
});

const comoCliente = c => ({
  chave: `cliente-${c.id}`,
  titulo: c.name || c.trade_name || t(`${B}.SEM_NOME`),
  subtitulo: c.cpf_cnpj || c.phone_number || '',
  selo: t(`${B}.SELO.CLIENTE`),
  ...rosto(c.name || c.trade_name || ''),
  rota: accountScopedRoute('legal_client_show', { clientId: c.id }),
});

const comoProcesso = p => ({
  chave: `processo-${p.id}`,
  titulo: p.title || t(`${B}.SEM_TITULO`),
  subtitulo: p.number_cnj || '',
  // A pasta vai à parte porque é por ela que o escritório chama o processo —
  // quem digita "261" e recebe só o CNJ conclui que a busca ignorou o pedido.
  pasta: p.folder_number || '',
  selo: p.contact_name || '',
  icone: 'i-ph-scales',
  tom: 'azul',
  rota: accountScopedRoute('legal_process_show', { processId: p.id }),
});

const comoContato = c => ({
  chave: `contato-${c.id}`,
  titulo: c.name || t(`${B}.SEM_NOME`),
  subtitulo: c.email || c.phone_number || '',
  selo: t(`${B}.SELO.CONTATO`),
  ...rosto(c.name || ''),
  rota: accountScopedRoute('contacts_edit', { contactId: c.id }),
});

// Compromisso ou tarefa da Agenda Jurídica. A agenda não tem página por
// atividade — o detalhe é modal —, então o link vai com `?atividade=` e a
// agenda abre o modal ao montar (o mesmo caminho dos comandos do chat interno).
const comoAtividade = a => {
  const ehTarefa = a.activity_type === 'task';
  return {
    chave: `atividade-${a.id}`,
    titulo: a.description || t(`${B}.SEM_TITULO`),
    subtitulo: [a.contact_name, a.start_at ? dataEscritorio(a.start_at) : '']
      .filter(Boolean)
      .join(' · '),
    pasta: a.folder_number || '',
    selo: t(`${B}.SELO.${ehTarefa ? 'TAREFA' : 'COMPROMISSO'}`),
    icone: ehTarefa ? 'i-ph-check-square' : 'i-ph-calendar-blank',
    // As cores da Agenda: compromisso azul, tarefa íris.
    tom: ehTarefa ? 'iris' : 'azul',
    rota: accountScopedRoute('appointments_agenda', {}, { atividade: a.id }),
  };
};

// Publicação, documento e caso não têm ficha própria: o resultado abre a LISTA
// já filtrada pelo termo (`useBuscaDaRota` do outro lado).
const listaFiltrada = (nome, busca) =>
  accountScopedRoute(nome, {}, { busca: busca || termo.value.trim() });

const comoPublicacao = p => ({
  chave: `publicacao-${p.id}`,
  titulo:
    [p.tribunal, p.number_cnj].filter(Boolean).join(' · ') ||
    t(`${B}.SEM_TITULO`),
  // O TRECHO que casou, e não o começo: todo diário começa igual.
  subtitulo: p.trecho || '',
  selo: p.publication_date ? dataEscritorio(p.publication_date) : '',
  icone: 'i-ph-newspaper',
  tom: 'neutro',
  rota: listaFiltrada('legal_publications_index'),
});

const comoDocumento = d => ({
  chave: `documento-${d.id}`,
  titulo: d.title || d.file_name || t(`${B}.SEM_TITULO`),
  subtitulo: d.file_name || '',
  pasta: d.folder_number || '',
  selo: '',
  icone: fileFormatFor(d.file_name).icon,
  tom: 'neutro',
  rota: listaFiltrada('legal_ged_index', d.title),
});

const comoCaso = c => ({
  chave: `caso-${c.id}`,
  titulo: c.name || t(`${B}.SEM_TITULO`),
  subtitulo: '',
  pasta: c.folder_number || '',
  selo: '',
  icone: 'i-ph-briefcase',
  tom: 'azul',
  rota: listaFiltrada('legal_cases_index', c.name),
});

const comoLancamento = o => ({
  chave: `financeiro-${o.id}`,
  titulo: o.description || o.creditor_debtor_name || t(`${B}.SEM_TITULO`),
  subtitulo: [
    o.description ? o.creditor_debtor_name : '',
    o.due_date ? t(`${B}.VENCE_EM`, { data: dataEscritorio(o.due_date) }) : '',
  ]
    .filter(Boolean)
    .join(' · '),
  selo: moedaComSimbolo(o.gross_amount),
  icone: 'i-ph-currency-circle-dollar',
  tom: 'neutro',
  rota: accountScopedRoute('financial_obligation_show', {
    obligationId: o.id,
  }),
});

const comoContrato = c => ({
  chave: `contrato-${c.id}`,
  titulo: c.title || t(`${B}.SEM_TITULO`),
  subtitulo: c.contact_name || '',
  selo: '',
  icone: 'i-ph-signature',
  tom: 'neutro',
  rota: accountScopedRoute('legal_contracts_index'),
});

// Destinos (ferramentas). Filtrados aqui porque a lista já está na memória —
// ir ao servidor para saber que "Financeiro" contém "finan" seria uma consulta
// jogada fora a cada tecla.
const comoDestino = item => ({
  chave: `destino-${item.name}`,
  titulo: item.label,
  subtitulo: '',
  selo: '',
  icone: item.icon || 'i-ph-arrow-square-out',
  tom: 'neutro',
  rota: item.to,
});

// Sem acento e sem caixa dos dois lados: "financ" acha "Financeiro" e "agenda"
// acha "Agência". Mesma normalização do ranking, para os dois não divergirem.
const semAcento = texto =>
  String(texto ?? '')
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();

const pontuaNoTermo = texto =>
  semAcento(texto).includes(semAcento(termo.value).trim());

// Subtela de um módulo (Modelos de Minutas, Agentes de Relatórios): só entra
// quando há termo — sem termo, a lista é o mapa dos módulos, um por linha.
const comoSubtela = (pai, filho) => ({
  chave: `destino-${pai.name}-${filho.name}`,
  titulo: filho.label,
  subtitulo: pai.label,
  selo: '',
  icone: pai.icon || 'i-ph-arrow-square-out',
  tom: 'neutro',
  rota: filho.to,
});

// Sem termo, TODOS os módulos, na ordem e nos grupos da barra (08/10/2026):
// eram seis, cortados no meio da barra, e a barra só mostra ícones — aqui é
// o lugar em que cada um aparece com o nome.
const destinosVisiveis = computed(() => {
  const modulos = (props.destinos || []).filter(item => item.to);
  if (!termo.value.trim()) return modulos.map(comoDestino);

  const lista = modulos.flatMap(item =>
    (item.children || []).length
      ? [
          comoDestino(item),
          ...item.children.filter(f => f.to).map(f => comoSubtela(item, f)),
        ]
      : [comoDestino(item)]
  );
  return ranquear(lista, termo.value).filter(
    item =>
      item.titulo &&
      (pontuaNoTermo(item.titulo) || pontuaNoTermo(item.subtitulo))
  );
});

// CONVERSAS DO CHAT INTERNO (08/10/2026). A troca rápida de conversa do
// chat interno morava no ⌘K da paleta antiga — que abria POR BAIXO desta
// (Victor: "deveria aparecer só o nosso"). O atalho ficou só aqui, e as
// conversas vieram junto, da memória: a lista já está no store para o
// contador da barra. Mesma regra de nome da lateral do chat: canal com "#",
// conversa direta com o nome do OUTRO.
const conversasInternas = useMapGetter('internalChats/getChats');
const usuarioAtual = useMapGetter('getCurrentUser');

const comoConversaInterna = chat => {
  if (chat.chat_type === 'direct_message') {
    const outro =
      chat.other_user ||
      (chat.users || []).find(u => u.id !== usuarioAtual.value?.id);
    const nome = outro?.name || t('INTERNAL_CHATS.DIRECT_MESSAGE_FALLBACK');
    return {
      chave: `chat-${chat.id}`,
      titulo: nome,
      subtitulo: t(`${B}.CONVERSA_DIRETA`),
      selo: '',
      ...rosto(nome),
      rota: accountScopedRoute('internal_chat_detail', { chatId: chat.id }),
    };
  }
  const canal = chat.chat_type === 'channel';
  return {
    chave: `chat-${chat.id}`,
    titulo: canal ? `#${chat.name || ''}` : chat.name || '',
    subtitulo: '',
    selo: '',
    icone: canal ? 'i-ph-hash' : 'i-ph-users-three',
    tom: 'iris',
    rota: accountScopedRoute('internal_chat_detail', { chatId: chat.id }),
  };
};

const conversasVisiveis = computed(() => {
  if (!termo.value.trim()) return [];
  const lista = (conversasInternas.value || []).map(comoConversaInterna);
  return ranquear(lista, termo.value).filter(
    item => item.titulo && pontuaNoTermo(item.titulo)
  );
});

const ADAPTADORES = {
  clientes: comoCliente,
  processos: comoProcesso,
  atividades: comoAtividade,
  publicacoes: comoPublicacao,
  documentos: comoDocumento,
  casos: comoCaso,
  financeiro: comoLancamento,
  contratos: comoContrato,
  contatos: comoContato,
};

const grupos = computed(() => {
  const q = termo.value.trim();
  const corta = lista => lista.slice(0, LIMITE_POR_GRUPO);

  const todos = [
    {
      chave: 'destinos',
      rotulo: t(`${B}.GRUPO.IR_PARA`),
      // Sem termo, o mapa inteiro; com termo, o corte de todo grupo.
      itens: q ? corta(destinosVisiveis.value) : destinosVisiveis.value,
    },
    {
      chave: 'chat_interno',
      rotulo: t(`${B}.GRUPO.CHAT_INTERNO`),
      itens: corta(conversasVisiveis.value),
    },
    ...GRUPOS.map(chave => ({
      chave,
      rotulo: t(`${B}.GRUPO.${chave.toUpperCase()}`),
      itens: corta(ranquear(resultado.value[chave].map(ADAPTADORES[chave]), q)),
    })),
  ];

  // Por último, a busca DENTRO das mensagens (a tela de busca das
  // conversas): esta paleta acha gente, pasta e tarefa, não texto de
  // mensagem — e o escritório também procura "aquilo que o cliente disse".
  if (q.length >= MIN_CARACTERES) {
    todos.push({
      chave: 'mensagens',
      rotulo: t(`${B}.GRUPO.MENSAGENS`),
      itens: [
        {
          chave: 'mensagens-busca',
          titulo: t(`${B}.NAS_MENSAGENS`, { termo: q }),
          subtitulo: '',
          selo: '',
          icone: 'i-ph-chat-text',
          tom: 'neutro',
          rota: accountScopedRoute('search', {}, { q }),
        },
      ],
    });
  }

  return todos.filter(grupo => grupo.itens.length);
});

// A navegação por teclado anda numa lista ÚNICA, atravessando os grupos: o
// cabeçalho é visual, e parar a seta nele seria tratar decoração como conteúdo.
const itensNavegaveis = computed(() =>
  grupos.value.flatMap(grupo => grupo.itens)
);

const totalDeItens = computed(() => itensNavegaveis.value.length);
const temTermo = computed(() => termo.value.trim().length >= MIN_CARACTERES);
// A linha "procurar nas mensagens" é uma SAÍDA, não um resultado: com ela só,
// a tela continua dizendo que nada foi achado (e a linha fica embaixo).
const achados = computed(() =>
  grupos.value
    .filter(grupo => grupo.chave !== 'mensagens')
    .reduce((n, grupo) => n + grupo.itens.length, 0)
);
const semResultado = computed(
  () => temTermo.value && !carregando.value && !erro.value && !achados.value
);

// O campo é um `combobox` e a lista um `listbox`: quem usa leitor de tela
// ouve qual opção a seta escolheu, sem o foco sair do campo.
const idDaOpcao = indice => `busca-global-opcao-${indice}`;

const indiceGlobal = (grupoIndice, itemIndice) => {
  let antes = 0;
  for (let i = 0; i < grupoIndice; i += 1)
    antes += grupos.value[i].itens.length;
  return antes + itemIndice;
};

// ── busca ──────────────────────────────────────────────────────────────────
const buscar = async () => {
  const q = termo.value.trim();
  if (q.length < MIN_CARACTERES) {
    limpar();
    carregando.value = false;
    return;
  }

  geracao += 1;
  const minha = geracao;
  carregando.value = true;

  try {
    const { data } = await LegalProcessesApi.globalSearch(q, LIMITE_SERVIDOR);
    // Resposta atrasada de uma busca anterior não pode pintar a tela com o
    // termo novo escrito no campo.
    if (minha !== geracao) return;
    erro.value = false;
    resultado.value = Object.fromEntries(
      GRUPOS.map(g => [g, data.payload?.[g] || []])
    );
  } catch {
    if (minha === geracao) {
      limpar();
      erro.value = true;
    }
  } finally {
    if (minha === geracao) carregando.value = false;
  }
};

watch(termo, () => {
  indiceAtivo.value = 0;
  erro.value = false;
  clearTimeout(temporizador);
  if (termo.value.trim().length < MIN_CARACTERES) {
    limpar();
    carregando.value = false;
    return;
  }
  carregando.value = true;
  temporizador = setTimeout(buscar, DEBOUNCE_MS);
});

// ── abrir / fechar ─────────────────────────────────────────────────────────
watch(aberta, async valor => {
  if (!valor) {
    clearTimeout(temporizador);
    // A geração avança ao FECHAR: uma resposta em voo não pode encher a lista
    // depois que a pessoa já saiu.
    geracao += 1;
    termo.value = '';
    limpar();
    erro.value = false;
    carregando.value = false;
    return;
  }

  termo.value = termoInicial.value || '';
  indiceAtivo.value = 0;
  await nextTick();
  campoRef.value?.focus();
});

const irPara = item => {
  if (!item?.rota) return;
  fechar();
  router.push(item.rota);
};

const escolherAtivo = () => {
  const item = itensNavegaveis.value[indiceAtivo.value];
  if (item) irPara(item);
};

// Mantém a linha escolhida visível ao andar de seta — sem isso a seleção
// "some" para fora da área rolável e a pessoa navega às cegas.
const rolarParaAtivo = async () => {
  await nextTick();
  const alvo = listaRef.value?.querySelector('[aria-selected="true"]');
  alvo?.scrollIntoView({ block: 'nearest' });
};

const andar = passo => {
  if (!totalDeItens.value) return;
  // Dá a volta nas pontas: descer no último leva ao primeiro. Travar na ponta
  // faz a tecla parecer quebrada numa lista curta.
  indiceAtivo.value =
    (indiceAtivo.value + passo + totalDeItens.value) % totalDeItens.value;
  rolarParaAtivo();
};

const limparTermo = () => {
  termo.value = '';
  campoRef.value?.focus();
};

const aoTeclar = evento => {
  if (evento.key === 'ArrowDown') {
    evento.preventDefault();
    andar(1);
    return;
  }
  if (evento.key === 'ArrowUp') {
    evento.preventDefault();
    andar(-1);
    return;
  }
  if (evento.key === 'Enter') {
    evento.preventDefault();
    escolherAtivo();
  }
};

// O ⌘K é registrado aqui, e não em cada tela: um atalho global com dono único.
const aoTeclarNaJanela = evento => {
  if (!ehAtalhoDeBusca(evento)) return;
  evento.preventDefault();
  aberta.value = !aberta.value;
};

onMounted(() => window.addEventListener('keydown', aoTeclarNaJanela));
onUnmounted(() => {
  window.removeEventListener('keydown', aoTeclarNaJanela);
  clearTimeout(temporizador);
});
</script>

<template>
  <Teleport to="body">
    <div
      v-if="aberta"
      class="bg-fundo advup-modal-fundo advup-modal-fundo--topo"
      @click.self="fechar"
    >
      <!-- A CASCA É A DO PRODUTO: véu, folha, topo navy e a entrada com a
           curva do pulo vêm de `_advup-modal.scss`; papel de diálogo, foco,
           armadilha de Tab, Escape e devolução do foco, de `v-modal-acessivel`.
           O arquivo desenhava tudo isso sozinho — e esquecia a armadilha de
           Tab e a devolução do foco. -->
      <div
        v-modal-acessivel="fechar"
        class="bg-caixa advup-modal advup-modal--claro"
        :aria-label="t(`${B}.TITULO`)"
      >
        <div class="bg-topo advup-modal__topo">
          <!-- O topo da Agenda (guia §5.10): véu azul que escorre, selo
               sólido e a lupa grande e quase apagada no canto. -->
          <i
            class="advup-modal__marca bg-topo__marca i-ph-magnifying-glass"
            aria-hidden="true"
          />
          <span class="bg-topo__selo advup-modal__selo" aria-hidden="true">
            <i class="i-ph-magnifying-glass" />
          </span>
          <div class="bg-campo">
            <input
              ref="campoRef"
              v-model="termo"
              type="text"
              role="combobox"
              aria-autocomplete="list"
              aria-controls="busca-global-lista"
              :aria-expanded="totalDeItens > 0"
              :aria-activedescendant="
                totalDeItens ? idDaOpcao(indiceAtivo) : undefined
              "
              :aria-label="t(`${B}.CAMPO_CURTO`)"
              class="bg-campo__entrada reset-base"
              :placeholder="t(`${B}.PLACEHOLDER`)"
              autocomplete="off"
              spellcheck="false"
              @keydown="aoTeclar"
            />
            <span
              v-if="carregando"
              class="bg-campo__giro advup-gira i-ph-circle-notch"
              aria-hidden="true"
            />
            <button
              v-else-if="termo"
              type="button"
              class="bg-campo__limpar"
              :aria-label="t(`${B}.LIMPAR`)"
              @click="limparTermo"
            >
              <i class="i-ph-x" aria-hidden="true" />
            </button>
          </div>
          <button
            type="button"
            class="advup-modal__fechar"
            :aria-label="t(`${B}.FECHAR`)"
            @click="fechar"
          >
            <i class="i-ph-x" aria-hidden="true" />
          </button>
        </div>

        <div
          id="busca-global-lista"
          ref="listaRef"
          class="bg-corpo"
          role="listbox"
          :aria-label="t(`${B}.TITULO`)"
        >
          <div
            v-for="(grupo, gi) in grupos"
            :key="grupo.chave"
            class="advup-paleta__grupo"
            role="group"
            :aria-labelledby="`busca-global-grupo-${grupo.chave}`"
          >
            <p
              :id="`busca-global-grupo-${grupo.chave}`"
              class="advup-paleta__grupo-rotulo"
            >
              <span class="advup-paleta__grupo-nome">{{ grupo.rotulo }}</span>
              <span class="advup-conta" aria-hidden="true">
                {{ grupo.itens.length }}
              </span>
            </p>
            <div
              v-for="(item, ii) in grupo.itens"
              :id="idDaOpcao(indiceGlobal(gi, ii))"
              :key="item.chave"
              role="option"
              class="advup-paleta__linha advup-entra"
              :class="{
                'advup-paleta__linha--ativa':
                  indiceGlobal(gi, ii) === indiceAtivo,
              }"
              :aria-selected="indiceGlobal(gi, ii) === indiceAtivo"
              :style="{ '--i': Math.min(indiceGlobal(gi, ii), 8) }"
              @mousemove="indiceAtivo = indiceGlobal(gi, ii)"
              @click="irPara(item)"
            >
              <span
                v-if="item.pessoa"
                class="advup-avatar advup-avatar--m advup-paleta__rosto"
                :class="`advup-avatar--${item.cor}`"
                aria-hidden="true"
              >
                {{ item.iniciais }}
              </span>
              <span
                v-else
                class="advup-paleta__ladrilho"
                :class="`advup-paleta__ladrilho--${item.tom}`"
                aria-hidden="true"
              >
                <i :class="item.icone" />
              </span>

              <span class="advup-paleta__texto">
                <span class="advup-paleta__titulo">
                  <!-- O trecho casado vem em destaque: é o que explica por
                       que aquela linha apareceu. Mesmo `destacar` do menu de
                       barra do chat interno. -->
                  <span
                    v-for="(parte, pi) in destacar(item.titulo, termo)"
                    :key="pi"
                    :class="{ 'advup-paleta__forte': parte.forte }"
                    >{{ parte.texto }}</span
                  >
                </span>
                <span
                  v-if="item.subtitulo || item.pasta"
                  class="advup-paleta__sub"
                >
                  <span v-if="item.pasta" class="advup-etq advup-paleta__pasta">
                    <i class="i-ph-folder" aria-hidden="true" />
                    <span>{{ item.pasta }}</span>
                  </span>
                  <span v-if="item.subtitulo" class="advup-paleta__sub-txt">
                    {{ item.subtitulo }}
                  </span>
                </span>
              </span>

              <span v-if="item.selo" class="advup-etq advup-paleta__selo">
                <span>{{ item.selo }}</span>
              </span>
              <kbd
                class="advup-paleta__tecla advup-paleta__enter"
                aria-hidden="true"
              >
                {{ TECLA_ENTRA }}
              </kbd>
            </div>
          </div>

          <!-- A falha, com nome e com saída. -->
          <div v-if="erro" class="advup-vazio advup-vazio--erro">
            <span class="advup-vazio__icone" aria-hidden="true">
              <i class="i-ph-warning-circle" />
            </span>
            <p class="advup-vazio__tit">
              {{ t('LEGAL_MODULE.ERROR_STATE.SECTION_TITLE') }}
            </p>
            <p class="advup-vazio__dica">
              {{ t('LEGAL_MODULE.ERROR_STATE.SECTION_HINT') }}
            </p>
            <div class="advup-vazio__acao">
              <button
                type="button"
                class="advup-btn advup-btn--vazado"
                @click="buscar"
              >
                <i class="i-ph-arrow-clockwise" aria-hidden="true" />
                <span>{{ t('LEGAL_MODULE.ERROR_STATE.RETRY') }}</span>
              </button>
            </div>
          </div>

          <div v-else-if="semResultado" class="advup-vazio">
            <span class="advup-vazio__icone" aria-hidden="true">
              <i class="i-ph-magnifying-glass-minus" />
            </span>
            <p class="advup-vazio__tit">
              {{ t(`${B}.VAZIO`, { termo: termo.trim() }) }}
            </p>
            <p class="advup-vazio__dica">{{ t(`${B}.VAZIO_DICA`) }}</p>
          </div>

          <!-- Procurando, sem nada ainda na tela: o esqueleto do produto, e
               não a palavra "Procurando" sozinha no branco. -->
          <EsqueletoLista
            v-else-if="carregando && !totalDeItens"
            :linhas="3"
            :altura="48"
            :rotulo="t(`${B}.PROCURANDO`)"
          />
        </div>

        <!-- Rodapé de atalhos -->
        <div class="advup-paleta__rodape" aria-hidden="true">
          <span class="advup-paleta__atalho">
            <kbd class="advup-paleta__tecla">{{ TECLA_CIMA }}</kbd>
            <kbd class="advup-paleta__tecla">{{ TECLA_BAIXO }}</kbd>
            {{ t(`${B}.NAVEGAR`) }}
          </span>
          <span class="advup-paleta__atalho">
            <kbd class="advup-paleta__tecla">{{ TECLA_ENTRA }}</kbd>
            {{ t(`${B}.ABRIR`) }}
          </span>
          <span class="advup-paleta__atalho">
            <kbd class="advup-paleta__tecla">{{ TECLA_ESC }}</kbd>
            {{ t(`${B}.FECHAR`) }}
          </span>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
/* ── A PALETA DE BUSCA NA CASCA DO PRODUTO (14/09/2026) ──────────────────
 * Victor: "deixar no mesmo padrão, animações e tudo mais que os outros
 * módulos". O arquivo desenhava a própria casca — 79 itens fora do padrão:
 * 17 durações e 15 curvas cravadas, 17 tamanhos de letra fora da escala, 20
 * espaçamentos fora da grade. E três defeitos na tela, medidos antes de
 * mexer:
 *   - a linha ativa andava 2px para o lado (`translate`) dentro de uma lista
 *     de largura cheia: 640px de conteúdo em 638, e a barra de rolagem
 *     horizontal ficava visível embaixo da lista;
 *   - os processos pintavam o ladrilho com `--indigo-9`, que NÃO EXISTE no
 *     produto — o ícone ficava sem fundo e o ponto do grupo, invisível;
 *   - no claro, 15 textos abaixo do mínimo (cinza `slate-10` sobre o véu).
 * O que fica aqui é só o que é desta tela: onde a folha nasce, o campo dentro
 * do topo navy e a linha de resultado. */

/* A paleta nasce no alto, não no meio: quem busca olha para o topo. */
.bg-fundo {
  --modal-respiro: 12vh var(--esp-4) var(--esp-4);

  align-items: flex-start;
}

.bg-caixa {
  --modal-larg: 40rem;

  max-height: min(72vh, 40rem);
  margin: 0 auto;
}

/* ── o topo: o campo branco no véu claro (08/10/2026) ──────────────────
   Victor: "o modal tem que ficar estilo o novo modal que a gente construiu"
   — a faixa navy com o campo rebaixado era o topo escuro que o guia (§5.10)
   aposentou. Agora é a casca clara da Agenda (`advup-modal--claro`) e o
   campo é um campo de verdade: branco, fio fino, anel azul no foco. */
.bg-topo {
  gap: var(--esp-3);
  padding: var(--esp-4) var(--esp-3) var(--esp-4) var(--esp-4);
}

/* O campo ocupa o meio do topo e é opaco: a lupa d'água vai para o canto
   do fechar, o único papel livre (à esquerda ela saía cortada atrás do
   selo, um arco solto). */
.bg-topo__marca {
  top: -46px;
  right: -40px;
}

.bg-campo {
  position: relative;
  display: flex;
  flex: 1;
  align-items: center;
  gap: var(--esp-2);
  min-width: 0;
  height: 46px;
  padding: 0 var(--esp-2) 0 var(--esp-3);
  border-radius: var(--r-4);
  background: rgb(var(--solid-1));
  box-shadow:
    inset 0 0 0 1px rgb(var(--slate-6)),
    0 1px 2px rgb(15 23 42 / 0.06);
  transition: box-shadow var(--mov-2) var(--mov-curva);
}

.bg-campo:hover {
  box-shadow:
    inset 0 0 0 1px rgb(var(--slate-7)),
    0 1px 2px rgb(15 23 42 / 0.06);
}

.bg-campo:focus-within {
  box-shadow:
    inset 0 0 0 1px rgb(var(--blue-8)),
    0 0 0 3px rgb(var(--blue-9) / 0.18);
}

.dark .bg-campo {
  background: rgb(var(--solid-2));
}

.bg-campo__entrada {
  /* O campo ocupa a altura inteira (46px): tocar em qualquer ponto
     escreve. */
  align-self: stretch;
  flex: 1;
  min-width: 0;
  padding: 0;
  border: 0;
  background: transparent;
  font-size: var(--txt-lista);
  line-height: var(--linha-apertada);
  color: rgb(var(--slate-12));
}

.bg-campo__entrada:focus {
  outline: none;
}

.bg-campo__entrada::placeholder {
  color: rgb(var(--slate-10));
}

.bg-campo__giro {
  flex-shrink: 0;
  width: var(--ico-2);
  height: var(--ico-2);
  color: rgb(var(--blue-9));
}

.bg-campo__limpar {
  position: relative;
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  border-radius: var(--r-redondo);
  background: rgb(var(--slate-3));
  color: rgb(var(--slate-11));
  cursor: pointer;
  transition:
    background-color var(--mov-1) var(--mov-curva),
    color var(--mov-1) var(--mov-curva);
}

.bg-campo__limpar:hover {
  background: rgb(var(--slate-4));
  color: rgb(var(--slate-12));
}

.bg-campo__limpar:focus-visible {
  outline: 2px solid rgb(var(--blue-9));
  outline-offset: 1px;
}

.bg-campo__limpar > i {
  display: block;
  font-size: var(--ico-1);
  line-height: 1;
}

/* ── o corpo ──────────────────────────────────────────────────────────── */
/* Papel liso: o véu do topo já dá a cor. O brilho radial que havia aqui é
   "gradiente radial de fundo", anti-padrão do guia (§9). `overflow-x:
   hidden` segura a barra de rolagem lateral. */
.bg-corpo {
  flex: 1;
  min-height: 0;
  overflow: hidden auto;
  padding: var(--esp-1) var(--esp-2) var(--esp-2);
  background: rgb(var(--solid-1));
}

/* No celular a paleta ocupa a tela de ponta a ponta, o rodapé some (fala de
   teclas que não existem) e os alvos crescem para o dedo. */
@media (max-width: 639px) {
  .bg-fundo {
    --modal-respiro: var(--esp-2);
  }

  .bg-caixa {
    max-height: calc(100dvh - var(--esp-4));
  }

  .bg-topo {
    padding: var(--esp-2) var(--esp-2) var(--esp-2) var(--esp-3);
  }

  .bg-topo__selo {
    display: none;
  }

  .bg-campo__limpar::before {
    content: '';
    position: absolute;
    inset: -8px;
  }
}
</style>
